'use es6';

import styled from 'foundations-theming/utils';
import { Fragment, useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import EPHeader from 'editor-components/header/EPHeader';
import EPNavigationBar from 'editor-components/header/EPNavigationBar';
import { NavMarker } from 'react-rhumb';
import UsageTracker, { useTracker } from 'ContentEditorUI/utils/UsageTracker';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIButton from 'UIComponents/button/UIButton';
import { MODAL_TYPES } from 'ContentEditorUI/lib/modalConstants';
import StyleBreakPointsControlsContainer from 'ContentEditorUI/containers/StyleBreakPointsControlsContainer';
import TranslationSelectorContainer from 'ContentEditorUI/containers/TranslationSelectorContainer';
import ExitButton from 'ContentEditorUI/components/header/ExitButton';
import ExitDeveloperModeButton from 'ContentEditorUI/components/ExitDeveloperModeButton';
import EPSettingsButton from 'editor-components/header/EPSettingsButton';
import UndoRedoButtonContainer from 'ContentEditorUI/containers/UndoRedoButtonContainer';
import BlogSaveButtonContainer from '../../containers/BlogSaveButtonContainer';
import DistractionFreeToggle from '../DistractionFreeToggle';
import { getIsInDeveloperMode, getIsReadOnlyMode } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { getHasUndoRedoAccess } from 'ContentEditorUI/redux/selectors/moduleSelectors';
import { getHasUnsavedChanges } from 'ContentEditorUI/redux/selectors/saveDataSelectors';
import { getShouldClearHeaderActiveItem } from 'ContentEditorUI/redux/selectors/headerSelectors';
import { getShouldUseResponsiveBreakpoints } from 'ContentEditorUI/redux/selectors/styleBreakpointsSelectors';
import { getDashboardUrl } from '../../redux/selectors/urlSelectors';
import { showLoading, hideLoading, openModal } from 'ContentEditorUI/redux/actions/appActions';
import { routeToPage } from 'ContentEditorUI/redux/actions/routingActions';
import { toggleDistractionFree as toggleDistractionFreeAction } from 'ContentEditorUI/redux/actions/distractionFreeActions';
import { setShouldClearHeaderActiveItem, updateHeaderActiveItem } from 'ContentEditorUI/redux/actions/headerActions';
import { setScrollTo } from 'ContentEditorUI/redux/actions/baseSettingsActions';
import { save } from '../../redux/actions/postSaveActions';
import { useNavigationGroups } from '../../hooks/useNavigationGroups';
import { getIsDistractionFreeOpen } from 'ContentEditorUI/redux/selectors/localCachedSettingsSelectors';
import { getCurrentTab } from 'ContentEditorUI/redux/selectors/universalNavSelectors';
import UIAutosizedTextInput from 'UIComponents/input/UIAutosizedTextInput';
import { getContentModelName, getPublishDate, getContentModelId } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { updateTitle } from '../../redux/actions/postContentModelActions';
import { OnboardingUITourStepPostName, OnboardingUITourStepPublish } from '../onboarding/OnboardingTourSteps';
import BlogPublishButtonContainer from '../../containers/BlogPublishButtonContainer';
import ContentPublishStatus from 'ContentEditorUI/components/shared/ContentPublishStatus';
import { getHardSavedContentModelIsPublishedNotScheduled, getHardSavedContentModelIsScheduled, getHardSavedContentModelScheduledUpdateDate } from 'ContentEditorUI/redux/selectors/hardSavedContentModelSelectors';
import ContentCrmObjectProvider from 'ContentEditorUI/components/publishing/ContentCrmObjectProvider';
import { ContentObjectTypes } from 'content-management-data-lib';
import { getIsUngatedForCMPPublishing } from '../../redux/selectors/authSelectors';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TabStartSlotWrapper = styled.div.withConfig({
  displayName: "PatagoniaHeader__TabStartSlotWrapper",
  componentId: "sc-1feq69g-0"
})([".tab-slot-start-flex{span{font-weight:normal;}& > *:first-child{border-right:1px solid ", ";}& > *:only-child{border-right:none;}}"], ({
  theme
}) => theme.color['do-not-use-battleship']);
const EditorToolbarEndSlot = () => {
  const dispatch = useDispatch();
  const hasUnsavedChanges = useSelector(getHasUnsavedChanges);
  const isInDeveloperMode = useSelector(getIsInDeveloperMode);
  const handleSaveClick = useCallback(() => {
    dispatch(save());
    UsageTracker.trackEditorInteraction({
      action: 'Revisions Action',
      event: 'clicked-save'
    });
  }, [dispatch]);
  const handlePreviewClick = useCallback(() => {
    if (hasUnsavedChanges) {
      dispatch(showLoading());
      dispatch(save()).finally(() => {
        dispatch(hideLoading());
        dispatch(routeToPage('preview'));
      }).catch(error => console.error(error));
    } else {
      dispatch(routeToPage('preview'));
    }
  }, [dispatch, hasUnsavedChanges]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [isInDeveloperMode && /*#__PURE__*/_jsx(ExitDeveloperModeButton, {}), /*#__PURE__*/_jsx(BlogSaveButtonContainer, {
      onSaveClick: handleSaveClick
    }), /*#__PURE__*/_jsx(UIButton, {
      onClick: handlePreviewClick,
      use: "secondary-ghost",
      size: "small",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "components.tabBar.preview"
      })
    }), /*#__PURE__*/_jsx(OnboardingUITourStepPublish, {
      children: /*#__PURE__*/_jsx(BlogPublishButtonContainer, {})
    })]
  });
};
const PatagoniaHeader = /*#__PURE__*/memo(({
  toggleModuleEditSidebarCallback
}) => {
  const dispatch = useDispatch();
  const currentTab = useSelector(getCurrentTab);
  const dashboardUrl = useSelector(getDashboardUrl);
  const hasUndoRedoAccess = useSelector(getHasUndoRedoAccess);
  const isDistractionFreeOpen = useSelector(getIsDistractionFreeOpen);
  const isReadOnlyMode = useSelector(getIsReadOnlyMode);
  const shouldUseResponsiveBreakpoints = useSelector(getShouldUseResponsiveBreakpoints);
  const pageName = useSelector(getContentModelName);
  const shouldClearHeader = useSelector(getShouldClearHeaderActiveItem);
  const tracker = useTracker();
  const navigationGroups = useNavigationGroups();
  const HEADER_CLASSNAME = 'fullscreen-editor-header';
  const handleToggleDistractionFree = useCallback(() => dispatch(toggleDistractionFreeAction(!isDistractionFreeOpen)), [dispatch, isDistractionFreeOpen]);
  const handleActiveItemChange = useCallback(activeItem => {
    dispatch(updateHeaderActiveItem(activeItem));
    if (activeItem) {
      dispatch(setShouldClearHeaderActiveItem(false));
    }
  }, [dispatch]);
  const setSettingsModalOpenWithNavItem = useCallback(navItem => {
    dispatch(openModal(MODAL_TYPES.SETTINGS_MODAL));
    dispatch(setScrollTo(navItem));
  }, [dispatch]);
  const handleChangeName = useCallback(event => dispatch(updateTitle(event.target.value)), [dispatch]);
  const isScheduled = useSelector(getHardSavedContentModelIsScheduled);
  const isPublished = useSelector(getHardSavedContentModelIsPublishedNotScheduled);
  const scheduledUpdateDate = useSelector(getHardSavedContentModelScheduledUpdateDate);
  const publishDate = useSelector(getPublishDate);
  const shouldUseCMPPublicationAPI = useSelector(getIsUngatedForCMPPublishing);
  const contentCrmObjectId = useSelector(getContentModelId);
  return /*#__PURE__*/_jsx(ContentCrmObjectProvider, {
    contentObjectType: ContentObjectTypes.BLOG_POST,
    contentCrmObjectId: contentCrmObjectId,
    enableScheduledPublishing: shouldUseCMPPublicationAPI,
    shouldUseContentCrmObject: shouldUseCMPPublicationAPI,
    children: /*#__PURE__*/_jsx(EPHeader, {
      editorToolbarClassName: HEADER_CLASSNAME,
      tabsClassName: "m-y-2",
      shouldAdaptForBranchPreview: true,
      titleText: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        direction: "column",
        children: [/*#__PURE__*/_jsx(OnboardingUITourStepPostName, {
          children: /*#__PURE__*/_jsx(UIAutosizedTextInput, {
            affordance: true,
            onChange: handleChangeName,
            use: "on-dark",
            value: pageName,
            readOnly: isReadOnlyMode
          })
        }), /*#__PURE__*/_jsx(ContentPublishStatus, {
          isPublished: isPublished,
          isScheduled: isScheduled,
          publishDate: publishDate,
          scheduledUpdateDate: scheduledUpdateDate
        })]
      }),
      editorToolbarStartSlot: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ExitButton, {
          dashboardUrl: dashboardUrl
        }), /*#__PURE__*/_jsx(NavMarker, {
          name: "PARENT_APP_CHROME_RENDERED"
        }), /*#__PURE__*/_jsx(EPNavigationBar, {
          navigationGroups: navigationGroups,
          onActiveItemChange: handleActiveItemChange,
          shouldClear: shouldClearHeader
        })]
      }),
      editorToolbarEndSlot: /*#__PURE__*/_jsx(EditorToolbarEndSlot, {}),
      tabsStartSlot: /*#__PURE__*/_jsx(TabStartSlotWrapper, {
        children: /*#__PURE__*/_jsx(Flex, {
          align: "center",
          justify: "start",
          className: "tab-slot-start-flex",
          children: /*#__PURE__*/_jsx(TranslationSelectorContainer, {})
        })
      }),
      tabsMiddleSlot: /*#__PURE__*/_jsx(Fragment, {
        children: shouldUseResponsiveBreakpoints && /*#__PURE__*/_jsx(StyleBreakPointsControlsContainer, {
          toggleModuleEditSidebarCallback: toggleModuleEditSidebarCallback
        })
      }),
      tabsEndSlot: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "end",
        children: [/*#__PURE__*/_jsx(DistractionFreeToggle, {
          isOpen: isDistractionFreeOpen,
          onChange: handleToggleDistractionFree,
          disabled: currentTab.id !== 'content',
          labelKey: 'focus'
        }), tracker && /*#__PURE__*/_jsx(EPSettingsButton, {
          onClick: () => setSettingsModalOpenWithNavItem('general'),
          usageTracker: tracker
        }), hasUndoRedoAccess && /*#__PURE__*/_jsx(View, {
          paddingInlineStart: "200",
          children: /*#__PURE__*/_jsx(UndoRedoButtonContainer, {
            isVisible: true,
            disabled: isReadOnlyMode
          })
        })]
      })
    })
  });
});
export default PatagoniaHeader;